import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Car, Zap, Shield, Gauge, Award } from 'lucide-react';

const AdPage4 = () => {
  const [activeFeature, setActiveFeature] = useState(0);
  const [screenWidth, setScreenWidth] = useState(1920); // Default fallback width

  const features = [
    { icon: Zap, title: 'Electric Power', desc: '0-60 in 2.1 seconds' },
    { icon: Shield, title: 'Advanced Safety', desc: '5-star safety rating' },
    { icon: Gauge, title: 'Performance', desc: '500+ mile range' },
    { icon: Award, title: 'Award Winning', desc: 'Car of the Year 2024' }
  ];

  // ✅ Handle window width safely
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setScreenWidth(window.innerWidth);

      const handleResize = () => setScreenWidth(window.innerWidth);
      window.addEventListener('resize', handleResize);

      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden relative mt-14">
      {/* Dynamic Background */}
      <motion.div
        animate={{
          background: [
            'radial-gradient(circle at 20% 80%, rgba(0, 255, 255, 0.1) 0%, transparent 50%)',
            'radial-gradient(circle at 80% 20%, rgba(255, 0, 255, 0.1) 0%, transparent 50%)',
            'radial-gradient(circle at 50% 50%, rgba(255, 255, 0, 0.1) 0%, transparent 50%)',
            'radial-gradient(circle at 20% 80%, rgba(0, 255, 255, 0.1) 0%, transparent 50%)'
          ]
        }}
        transition={{ duration: 8, repeat: Infinity }}
        className="absolute inset-0"
      />

      {/* Speed Lines */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute h-px bg-gradient-to-r from-transparent via-cyan-400 to-transparent"
            style={{
              top: `${Math.random() * 100}%`,
              width: `${Math.random() * 200 + 100}px`,
              left: '-200px'
            }}
            animate={{
              x: [0, screenWidth + 200],
              opacity: [0, 1, 0]
            }}
            transition={{
              duration: Math.random() * 2 + 1,
              repeat: Infinity,
              delay: Math.random() * 2
            }}
          />
        ))}
      </div>

      <div className="relative z-10 min-h-screen flex items-center">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Side - Car Showcase */}
          <motion.div
            initial={{ x: -100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 1 }}
            className="relative"
          >
            <motion.div whileHover={{ scale: 1.02 }} className="relative">
              <motion.div
                animate={{
                  boxShadow: [
                    '0 0 50px rgba(0, 255, 255, 0.3)',
                    '0 0 100px rgba(0, 255, 255, 0.6)',
                    '0 0 50px rgba(0, 255, 255, 0.3)'
                  ]
                }}
                transition={{ duration: 2, repeat: Infinity }}
                className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl p-8 relative overflow-hidden"
              >
                <div className="flex items-center justify-center h-64">
                  <motion.div
                    animate={{
                      scale: [1, 1.05, 1],
                      rotate: [0, 1, -1, 0]
                    }}
                    transition={{ duration: 4, repeat: Infinity }}
                  >
                    <Car className="w-48 h-48 text-cyan-400" />
                  </motion.div>
                </div>

                {/* Glowing Effect */}
                <motion.div
                  animate={{
                    background: [
                      'linear-gradient(90deg, transparent, rgba(0, 255, 255, 0.2), transparent)',
                      'linear-gradient(90deg, transparent, rgba(255, 0, 255, 0.2), transparent)',
                      'linear-gradient(90deg, transparent, rgba(0, 255, 255, 0.2), transparent)'
                    ]
                  }}
                  transition={{ duration: 3, repeat: Infinity }}
                  className="absolute inset-0 opacity-50"
                />
              </motion.div>
            </motion.div>

            {/* Feature Pills */}
            <div className="absolute -right-4 top-1/2 transform -translate-y-1/2 space-y-4">
              {features.map((feature, index) => {
                const FeatureIcon = feature.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ x: 50, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                    className={`p-3 rounded-full cursor-pointer transition-all duration-300 ${
                      activeFeature === index
                        ? 'bg-cyan-500 scale-110'
                        : 'bg-gray-800 hover:bg-gray-700'
                    }`}
                    onClick={() => setActiveFeature(index)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <FeatureIcon className="w-6 h-6" />
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Right Side - Content */}
          <motion.div
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="space-y-8"
          >
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <motion.h1
                className="text-7xl font-black mb-4"
                animate={{
                  textShadow: [
                    '0 0 20px rgba(0, 255, 255, 0.5)',
                    '0 0 40px rgba(0, 255, 255, 0.8)',
                    '0 0 20px rgba(0, 255, 255, 0.5)'
                  ]
                }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                VELOCITY
              </motion.h1>
              <motion.p
                animate={{
                  color: ['#ffffff', '#00ffff', '#ffffff']
                }}
                transition={{ duration: 3, repeat: Infinity }}
                className="text-2xl font-light tracking-wider"
              >
                THE FUTURE OF DRIVING
              </motion.p>
            </motion.div>

            {/* Active Feature Display */}
            <motion.div
              key={activeFeature}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-cyan-500/30"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-cyan-500 rounded-xl">
                  {(() => {
                    const ActiveFeatureIcon = features[activeFeature].icon;
                    return <ActiveFeatureIcon className="w-6 h-6" />;
                  })()}
                </div>
                <h3 className="text-2xl font-bold">{features[activeFeature].title}</h3>
              </div>
              <p className="text-gray-300 text-lg">{features[activeFeature].desc}</p>
            </motion.div>

            {/* CTA Section */}
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="space-y-4"
            >
              <motion.button
                whileHover={{
                  scale: 1.05,
                  boxShadow: '0 0 30px rgba(0, 255, 255, 0.5)'
                }}
                whileTap={{ scale: 0.95 }}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-black font-bold py-4 px-8 rounded-full text-xl shadow-2xl"
              >
                RESERVE NOW - $1,000 DEPOSIT
              </motion.button>

              <div className="flex justify-between text-sm text-gray-400">
                <span>Starting at $79,999</span>
                <span>Delivery: Q2 2024</span>
              </div>
            </motion.div>

            {/* Specs */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
              className="grid grid-cols-2 gap-4 text-center"
            >
              {[
                { label: 'Top Speed', value: '200 MPH' },
                { label: 'Range', value: '500 MI' },
                { label: 'Charge Time', value: '15 MIN' },
                { label: 'Horsepower', value: '1,000 HP' }
              ].map((spec, index) => (
                <motion.div
                  key={index}
                  whileHover={{ scale: 1.05 }}
                  className="bg-gray-900/30 rounded-xl p-4 border border-gray-700"
                >
                  <div className="text-2xl font-bold text-cyan-400">{spec.value}</div>
                  <div className="text-gray-400">{spec.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AdPage4;
